import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Button, Badge, Modal, ProgressBar, Form, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { useDispatch } from "react-redux";
import { Fn_DeleteData, Fn_FillListData } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import { toast } from "react-toastify";
import useSession from "../utils/useSession";
import { decryptPassword } from "../../helpers/SecurityHelper";

// ─── Multi-Tier Persistent Cache (RAM + IndexedDB) ───────────────────────────
const DB_NAME = "PayrollEmployeeDB";
const STORE_NAME = "employee_grid_cache_v3";
const LOG_STORE_NAME = "employee_delete_logs";
const DB_VERSION = 6;

const getIDB = () => {
  return new Promise((resolve) => {
    if (typeof window === "undefined" || !window.indexedDB) return resolve(null);
    try {
      const req = window.indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (e) => {
        try {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, { keyPath: "firmId" });
          }
          if (!db.objectStoreNames.contains(LOG_STORE_NAME)) {
            db.createObjectStore(LOG_STORE_NAME, { keyPath: "id" });
          }
        } catch (err) {}
      };
      req.onsuccess = () => {
        const db = req.result;
        if (!db || !db.objectStoreNames || !db.objectStoreNames.contains(STORE_NAME)) {
          resolve(null);
        } else {
          resolve(db);
        }
      };
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch (err) {
      resolve(null);
    }
  });
};

const readEmployeeCache = async (firmId) => {
  if (!firmId) return null;
  if (window.__empGridRAMCache && window.__empGridRAMCache[firmId]) {
    return window.__empGridRAMCache[firmId];
  }
  try {
    const db = await getIDB();
    if (!db || !db.objectStoreNames || !db.objectStoreNames.contains(STORE_NAME)) return null;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(Number(firmId));
        req.onsuccess = () => {
          const res = req.result ? req.result.data : null;
          if (res && Array.isArray(res) && res.length > 0) {
            if (!window.__empGridRAMCache) window.__empGridRAMCache = {};
            window.__empGridRAMCache[firmId] = res;
            resolve(res);
          } else {
            resolve(null);
          }
        };
        req.onerror = () => resolve(null);
      } catch (err) {
        resolve(null);
      }
    });
  } catch (e) {
    return null;
  }
};

const saveEmployeeCache = async (firmId, cleanList) => {
  if (!firmId || !Array.isArray(cleanList)) return;
  if (!window.__empGridRAMCache) window.__empGridRAMCache = {};
  window.__empGridRAMCache[firmId] = cleanList;
  try {
    const db = await getIDB();
    if (!db || !db.objectStoreNames.contains(STORE_NAME)) return;
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.put({ firmId: Number(firmId), data: cleanList, updatedAt: Date.now() });
  } catch (e) {}
};

export const clearEmployeeCache = async (firmId) => {
  if (typeof window !== "undefined") {
    if (window.__empGridRAMCache) {
      if (firmId) {
        delete window.__empGridRAMCache[firmId];
        delete window.__empGridRAMCache[Number(firmId)];
      } else {
        window.__empGridRAMCache = {};
      }
    }
    try {
      const db = await getIDB();
      if (db && db.objectStoreNames && db.objectStoreNames.contains(STORE_NAME)) {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        if (firmId) {
          store.delete(Number(firmId));
        } else {
          store.clear();
        }
      }
    } catch (_) {}
  }
};

// ─── Audit Logging Helpers (LocalStorage + IndexedDB Persistent) ─────────────
const readDeleteLogs = async (firmId) => {
  const localKey = `EMPLOYEE_DELETE_AUDIT_LOGS_${firmId || 0}`;
  let localLogs = [];
  try {
    const item = localStorage.getItem(localKey);
    if (item) localLogs = JSON.parse(item);
  } catch (e) {}

  try {
    const db = await getIDB();
    if (!db || !db.objectStoreNames.contains(LOG_STORE_NAME)) return localLogs;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(LOG_STORE_NAME, "readonly");
        const store = tx.objectStore(LOG_STORE_NAME);
        const req = store.getAll();
        req.onsuccess = () => {
          const idbLogs = (req.result || []).filter((l) => String(l.firmId) === String(firmId));
          const combined = [...localLogs, ...idbLogs];
          const uniqueMap = new Map();
          combined.forEach((l) => uniqueMap.set(l.id, l));
          const finalLogs = Array.from(uniqueMap.values()).sort((a, b) => b.timestamp - a.timestamp);
          resolve(finalLogs);
        };
        req.onerror = () => resolve(localLogs);
      } catch (err) {
        resolve(localLogs);
      }
    });
  } catch (e) {
    return localLogs;
  }
};

const saveDeleteLog = async (logEntry) => {
  if (!logEntry || !logEntry.firmId) return;
  const localKey = `EMPLOYEE_DELETE_AUDIT_LOGS_${logEntry.firmId}`;
  try {
    const existing = JSON.parse(localStorage.getItem(localKey) || "[]");
    const updated = [logEntry, ...existing].slice(0, 500); // keep last 500 logs
    localStorage.setItem(localKey, JSON.stringify(updated));
  } catch (e) {}

  try {
    const db = await getIDB();
    if (!db || !db.objectStoreNames.contains(LOG_STORE_NAME)) return;
    const tx = db.transaction(LOG_STORE_NAME, "readwrite");
    const store = tx.objectStore(LOG_STORE_NAME);
    store.put(logEntry);
  } catch (e) {}
};

/**
 * Trims raw employee record to lightweight table object for 60fps rendering
 */
const trimEmployeeRecord = (emp, index) => {
  const srNo = emp.SerialNo !== undefined && emp.SerialNo !== null ? emp.SerialNo : index + 1;
  const code = String(emp.EmployeeCode || emp.CardNo || "").trim();
  const name = String(emp.Name || "").trim();
  const father = String(emp.FatherName || "").trim();
  const dept = String(emp.DepartmentName || "").trim();
  const desig = String(emp.DesignationName || "").trim();
  let rawJoin = emp.JoinDate || emp.DateOfJoining || emp.JoiningDate || emp.DOJ || emp.Join_JoinDate || "";
  if (!rawJoin && emp.JoinJson) {
    try {
      const joinList = typeof emp.JoinJson === "string" ? JSON.parse(emp.JoinJson) : emp.JoinJson;
      if (Array.isArray(joinList) && joinList.length > 0) {
        const validJoins = joinList.filter((j) => j && j.JoinDate);
        if (validJoins.length > 0) {
          const sorted = [...validJoins].sort((a, b) => new Date(b.JoinDate) - new Date(a.JoinDate));
          rawJoin = sorted[0]?.JoinDate || "";
        }
      }
    } catch {}
  }
  let joinDate = "";
  if (rawJoin) {
    const s = String(rawJoin).split("T")[0].trim();
    if (s.includes("-")) {
      const parts = s.split("-");
      if (parts[0].length === 4) {
        // YYYY-MM-DD -> DD/MM/YYYY
        joinDate = `${parts[2]}/${parts[1]}/${parts[0]}`;
      } else if (parts[2].length === 4) {
        // DD-MM-YYYY -> DD/MM/YYYY
        joinDate = `${parts[0]}/${parts[1]}/${parts[2]}`;
      } else {
        joinDate = s.replace(/-/g, "/");
      }
    } else {
      joinDate = s;
    }
  }
  const empType = String(emp.EmployeeType || "").trim();
  const status = String(emp.WorkingStatus || emp.Status || "Working").trim();
  const sex = String(emp.Sex || "").trim();

  return {
    Id: emp.Id,
    SerialNo: srNo,
    EmployeeCode: code,
    Name: name,
    FatherName: father,
    DepartmentName: dept,
    DesignationName: desig,
    JoinDate: joinDate,
    EmployeeType: empType,
    WorkingStatus: status,
    Sex: sex,
    _search: `${srNo} ${code} ${name} ${father} ${dept} ${desig} ${joinDate} ${empType} ${status} ${sex}`.toLowerCase(),
  };
};

export const PageList_EmployeeMaster = () => {
  const [gridData, setGridData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Search & Filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: "SerialNo", direction: "asc" });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // ─── Checkbox Selection State ───
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isAllFilteredSelected, setIsAllFilteredSelected] = useState(false);

  // ─── Delete & Security Modal State ───
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: "", items: [], totalCount: 0 });
  const [securityPassword, setSecurityPassword] = useState("");
  const [deleteSecurityPassword, setDeleteSecurityPassword] = useState("015889");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0, percentage: 0 });

  // ─── Deletion Results Summary Modal ───
  const [showResultModal, setShowResultModal] = useState(false);
  const [deletionResults, setDeletionResults] = useState({
    successCount: 0,
    failedCount: 0,
    failedItems: [],
  });

  // ─── Audit Log History Modal ───
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditSearch, setAuditSearch] = useState("");
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { firm: currentFirm, firmId } = useSession();
  const passwordInputRef = useRef(null);

  const authUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails") || "{}");
    } catch {
      return {};
    }
  }, []);
  const authToken = authUser?.token || API_WEB_URLS.Token;
  const userId = authUser?.Id ? String(authUser.Id) : "0";
  const userName = authUser?.UserName || authUser?.Name || "Admin User";

  const LIST_API_URL = `${API_WEB_URLS.MASTER}/0/token/EmployeeDatabyFirm/Id/${firmId || 0}`;
  const DELETE_API_URL = `${API_WEB_URLS.MASTER}/0/token/EmployeeMaster`;

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim().toLowerCase());
    }, 200);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Load employee data from cache and server (Stale-While-Revalidate)
  const loadData = useCallback(
    async (forceRefresh = false) => {
      if (!firmId) {
        setGridData([]);
        setIsLoading(false);
        return;
      }

      // 1. Instant Cache Fill (0ms display while fetching fresh data)
      let hasCache = false;
      if (!forceRefresh) {
        try {
          const cached = await readEmployeeCache(firmId);
          if (cached && cached.length > 0) {
            setGridData(cached);
            hasCache = true;
          }
        } catch (e) {
          console.warn("Failed to read employee cache:", e);
        }
      }

      // 2. Fetch fresh data from server (keeps UI synchronized with DB)
      if (!hasCache) {
        setIsLoading(true);
      } else if (forceRefresh) {
        setIsRefreshing(true);
      }

      Fn_FillListData(
        dispatch,
        (rawList) => {
          if (Array.isArray(rawList)) {
            const cleanList = rawList.map(trimEmployeeRecord);
            setGridData(cleanList);
            saveEmployeeCache(firmId, cleanList);
          }
        },
        "gridData",
        LIST_API_URL
      )
        .catch((error) => {
          console.error("Failed to load employee masters:", error);
          if (!hasCache) {
            toast.error("Failed to load employees from server");
          }
        })
        .finally(() => {
          setIsLoading(false);
          setIsRefreshing(false);
        });
    },
    [dispatch, LIST_API_URL, firmId]
  );

  useEffect(() => {
    const shouldForce = !!location.state?.forceRefresh;
    loadData(shouldForce);
    setSelectedIds(new Set());
    setIsAllFilteredSelected(false);
  }, [loadData, location.state]);

  // Load master delete security password from PayrollParameter for current firm
  const loadSecurityPassword = useCallback(async () => {
    if (!firmId) return;
    try {
      const res = await Fn_FillListData(
        dispatch,
        () => {},
        "Data",
        `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${firmId}`
      );
      if (res && Array.isArray(res) && res.length > 0) {
        const rawPass = res[0]?.DeletePassword;
        if (rawPass) {
          const dec = await decryptPassword(rawPass);
          if (dec && dec.trim() !== "") {
            setDeleteSecurityPassword(dec.trim());
            return;
          }
        }
      }
      setDeleteSecurityPassword("015889");
    } catch (e) {
      console.error("Error loading delete security password from PayrollParameter:", e);
      setDeleteSecurityPassword("015889");
    }
  }, [dispatch, firmId]);

  useEffect(() => {
    loadSecurityPassword();
  }, [loadSecurityPassword]);

  // Load audit logs for the current firm
  const refreshAuditLogs = useCallback(async () => {
    if (!firmId) return;
    const logs = await readDeleteLogs(firmId);
    setAuditLogs(logs);
  }, [firmId]);

  useEffect(() => {
    if (showAuditModal) {
      refreshAuditLogs();
    }
  }, [showAuditModal, refreshAuditLogs]);

  // Navigation handlers
  const handleAdd = () => {
    navigate("/AddEmployee", { state: { Id: 0 } });
  };

  const handleEdit = (id) => {
    if (!id) return;
    navigate("/AddEmployee", { state: { Id: id } });
  };

  // Handle column header sorting
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  // Filter & Sort pipeline
  const filteredData = useMemo(() => {
    if (!gridData || gridData.length === 0) return [];

    let result = gridData;

    if (debouncedSearch) {
      result = result.filter((e) => e._search.includes(debouncedSearch));
    }

    if (sortConfig.key) {
      const { key, direction } = sortConfig;
      const factor = direction === "asc" ? 1 : -1;
      result = [...result].sort((a, b) => {
        let valA = a[key] ?? "";
        let valB = b[key] ?? "";

        if (typeof valA === "number" && typeof valB === "number") {
          return (valA - valB) * factor;
        }
        return String(valA).localeCompare(String(valB), undefined, { numeric: true, sensitivity: "base" }) * factor;
      });
    }

    return result;
  }, [gridData, debouncedSearch, sortConfig]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, pageSize]);

  // Pagination calculations
  const totalItems = filteredData.length;
  const totalPages = pageSize === -1 ? 1 : Math.ceil(totalItems / pageSize) || 1;
  const safePage = Math.min(currentPage, totalPages);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (safePage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, safePage, pageSize]);

  const startIndex = totalItems === 0 ? 0 : (safePage - 1) * (pageSize === -1 ? totalItems : pageSize) + 1;
  const endIndex = pageSize === -1 ? totalItems : Math.min(safePage * pageSize, totalItems);

  // ─── Checkbox Selection Handlers ───
  const pageIds = useMemo(() => paginatedData.map((d) => d.Id).filter(Boolean), [paginatedData]);

  const isPageFullySelected = useMemo(() => {
    if (pageIds.length === 0) return false;
    return pageIds.every((id) => selectedIds.has(id));
  }, [pageIds, selectedIds]);

  const isPagePartiallySelected = useMemo(() => {
    if (pageIds.length === 0) return false;
    const count = pageIds.filter((id) => selectedIds.has(id)).length;
    return count > 0 && count < pageIds.length;
  }, [pageIds, selectedIds]);

  const handleSelectPageToggle = () => {
    const next = new Set(selectedIds);
    if (isPageFullySelected) {
      pageIds.forEach((id) => next.delete(id));
      setIsAllFilteredSelected(false);
    } else {
      pageIds.forEach((id) => next.add(id));
    }
    setSelectedIds(next);
  };

  const handleSelectAllFiltered = () => {
    const allFilteredIds = filteredData.map((d) => d.Id).filter(Boolean);
    setSelectedIds(new Set(allFilteredIds));
    setIsAllFilteredSelected(true);
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
    setIsAllFilteredSelected(false);
  };

  const handleRowCheckbox = (id, e) => {
    e.stopPropagation();
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
      setIsAllFilteredSelected(false);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  // ─── Trigger Delete Workflows ───
  const promptSingleDelete = (row) => {
    loadSecurityPassword();
    setDeleteTarget({
      type: "SINGLE",
      items: [row],
      totalCount: 1,
    });
    setSecurityPassword("");
    setPasswordError("");
    setDeleteReason("");
    setShowDeleteModal(true);
  };

  const promptBulkDelete = () => {
    loadSecurityPassword();
    const items = gridData.filter((r) => selectedIds.has(r.Id));
    if (items.length === 0) {
      toast.warning("Please select at least one employee to delete.");
      return;
    }
    setDeleteTarget({
      type: "BULK",
      items: items,
      totalCount: items.length,
    });
    setSecurityPassword("");
    setPasswordError("");
    setDeleteReason("");
    setShowDeleteModal(true);
  };

  const promptDeleteAll = () => {
    loadSecurityPassword();
    const items = filteredData.length > 0 ? filteredData : gridData;
    if (items.length === 0) {
      toast.warning("No employees available to delete.");
      return;
    }
    setDeleteTarget({
      type: "ALL",
      items: items,
      totalCount: items.length,
    });
    setSecurityPassword("");
    setPasswordError("");
    setDeleteReason("");
    setShowDeleteModal(true);
  };

  // ─── Execute Secure Deletion ───
  const handleConfirmDelete = async () => {
    // 1. Password Verification against dynamic password from Payroll Parameter
    const entered = (securityPassword || "").trim();
    const authorized = (deleteSecurityPassword || "").trim() || "015889";

    if (entered !== authorized) {
      setPasswordError("Invalid security password! Please enter the authorized delete password configured in Payroll Parameter.");
      if (passwordInputRef.current) passwordInputRef.current.focus();
      return;
    }

    setPasswordError("");
    setIsDeleting(true);
    const targetItems = deleteTarget.items;
    const total = targetItems.length;
    setDeleteProgress({ current: 0, total, percentage: 0 });

    const successfullyDeletedIds = [];
    const failedList = [];

    try {
      // Chunk processing to prevent network bottlenecks while providing smooth progress UI
      const chunkSize = 5;
      for (let i = 0; i < targetItems.length; i += chunkSize) {
        const chunk = targetItems.slice(i, i + chunkSize);
        await Promise.all(
          chunk.map(async (emp) => {
            try {
              const res = await fetch(
                `${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/${userId}/token/EmployeeMaster/${emp.Id}`,
                {
                  method: "DELETE",
                  headers: {
                    Authorization: `Bearer ${authToken}`,
                    "Content-Type": "application/json",
                  },
                }
              );

              if (res.ok) {
                successfullyDeletedIds.push(emp.Id);
              } else {
                const errData = await res.json().catch(() => ({}));
                const msg = errData?.message || errData?.Message || `HTTP ${res.status}: Reference exists in salary/attendance.`;
                failedList.push({
                  employee: emp,
                  reason: msg,
                });
              }
            } catch (err) {
              failedList.push({
                employee: emp,
                reason: err.message || "Network error",
              });
            }
          })
        );

        const currentCount = Math.min(i + chunkSize, total);
        const pct = Math.round((currentCount / total) * 100);
        setDeleteProgress({ current: currentCount, total, percentage: pct });
      }

      // 2. Record Comprehensive Audit Log
      const now = new Date();
      const auditEntry = {
        id: `DEL_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        timestamp: Date.now(),
        dateStr: now.toLocaleString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }),
        firmId: firmId || 0,
        firmName: currentFirm?.Name || currentFirm?.FirmName || "Active Firm",
        userId: userId,
        userName: userName,
        actionType: deleteTarget.type === "SINGLE" ? "Single Delete" : deleteTarget.type === "BULK" ? "Bulk Selection Delete" : "Delete All Employees",
        totalRequested: total,
        successCount: successfullyDeletedIds.length,
        failedCount: failedList.length,
        reason: deleteReason.trim() || "Manual Deletion by User",
        verifiedPassword: securityPassword,
        deletedEmployees: targetItems
          .filter((item) => successfullyDeletedIds.includes(item.Id))
          .map((item) => ({
            id: item.Id,
            code: item.EmployeeCode,
            name: item.Name,
            fatherName: item.FatherName,
            dept: item.DepartmentName,
            desig: item.DesignationName,
            status: item.WorkingStatus,
          })),
        failedEmployees: failedList.map((f) => ({
          id: f.employee.Id,
          code: f.employee.EmployeeCode,
          name: f.employee.Name,
          reason: f.reason,
        })),
      };

      await saveDeleteLog(auditEntry);

      // 3. Optimistic RAM & Cache Update (0ms)
      if (successfullyDeletedIds.length > 0) {
        const deletedSet = new Set(successfullyDeletedIds);
        setGridData((prev) => {
          const updated = prev.filter((item) => !deletedSet.has(item.Id));
          saveEmployeeCache(firmId, updated);
          return updated;
        });

        // Clean selection set
        setSelectedIds((prev) => {
          const next = new Set(prev);
          successfullyDeletedIds.forEach((id) => next.delete(id));
          return next;
        });
        setIsAllFilteredSelected(false);
      }

      // Close delete modal
      setShowDeleteModal(false);

      // 4. Show Report or Toast Notification
      if (failedList.length > 0) {
        setDeletionResults({
          successCount: successfullyDeletedIds.length,
          failedCount: failedList.length,
          failedItems: failedList,
        });
        setShowResultModal(true);
      } else {
        toast.success(
          `🎉 Successfully deleted ${successfullyDeletedIds.length} employee record(s). Audit log saved.`
        );
      }
    } catch (err) {
      console.error("Bulk deletion failed:", err);
      toast.error(`Deletion process encountered an error: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Status Badge Helper
  const renderStatusBadge = (statusStr) => {
    const val = String(statusStr || "Working").trim();
    const lower = val.toLowerCase();
    let badgeClass = "badge badge-success light";
    if (lower === "left") badgeClass = "badge badge-danger light";
    else if (lower === "suspended") badgeClass = "badge badge-warning light";
    else if (lower === "retired") badgeClass = "badge badge-secondary light";
    return <span className={badgeClass}>{val}</span>;
  };

  // Sort Icon Helper
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <i className="fas fa-sort text-muted ms-1" style={{ fontSize: "11px", opacity: 0.4 }} />;
    }
    return sortConfig.direction === "asc" ? (
      <i className="fas fa-sort-up text-primary ms-1" style={{ fontSize: "12px" }} />
    ) : (
      <i className="fas fa-sort-down text-primary ms-1" style={{ fontSize: "12px" }} />
    );
  };

  // Filtered Delete Logs
  const filteredAuditLogs = useMemo(() => {
    if (!auditSearch.trim()) return auditLogs;
    const q = auditSearch.toLowerCase();
    return auditLogs.filter(
      (l) =>
        l.dateStr?.toLowerCase().includes(q) ||
        l.userName?.toLowerCase().includes(q) ||
        l.actionType?.toLowerCase().includes(q) ||
        l.reason?.toLowerCase().includes(q) ||
        l.deletedEmployees?.some((e) => e.name?.toLowerCase().includes(q) || e.code?.toLowerCase().includes(q))
    );
  }, [auditLogs, auditSearch]);

  // Export Audit Logs to CSV / JSON
  const handleExportAuditLogs = (format = "json") => {
    if (auditLogs.length === 0) {
      toast.warning("No delete audit logs to export.");
      return;
    }

    if (format === "json") {
      const blob = new Blob([JSON.stringify(auditLogs, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Employee_Delete_Audit_Logs_Firm_${firmId}_${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit logs exported to JSON.");
    } else {
      // CSV format
      const headers = ["Log ID", "Date & Time", "Action", "Deleted Count", "User", "Reason", "Deleted Employee Names & Codes"];
      const rows = auditLogs.map((l) => [
        `"${l.id}"`,
        `"${l.dateStr}"`,
        `"${l.actionType}"`,
        `"${l.successCount}"`,
        `"${l.userName}"`,
        `"${l.reason.replace(/"/g, '""')}"`,
        `"${(l.deletedEmployees || []).map((e) => `${e.code}-${e.name}`).join("; ")}"`,
      ]);
      const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Employee_Delete_Audit_Logs_Firm_${firmId}_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit logs exported to CSV.");
    }
  };

  return (
    <Container fluid className="page-content">
      {/* Top Header Bar */}
      <Row className="mb-3 align-items-center">
        <Col md="6">
          <div className="d-flex align-items-center gap-2">
            <h4 className="page-title mb-0" style={{ fontFamily: "Poppins", fontWeight: 600 }}>
              Employee Master
            </h4>
            {gridData.length > 0 && (
              <Badge bg="light" text="dark" className="border px-2 py-1" style={{ fontSize: "13px" }}>
                Total: <strong>{gridData.length.toLocaleString()}</strong>
              </Badge>
            )}
            {selectedIds.size > 0 && (
              <Badge bg="primary" className="px-2 py-1" style={{ fontSize: "13px" }}>
                Selected: <strong>{selectedIds.size.toLocaleString()}</strong>
              </Badge>
            )}
          </div>
        </Col>
        <Col md="6" className="text-end">
          <Button
            variant="outline-dark"
            size="sm"
            onClick={() => setShowAuditModal(true)}
            className="me-2"
            title="View historical deletion audit logs"
            style={{ fontWeight: 500 }}
          >
            <i className="fas fa-history me-1 text-primary" />
            Delete Logs / Audit Trail
          </Button>
        </Col>
      </Row>

      {/* Sticky Global Search & Action Buttons Container */}
      <div
        className="card mb-3 shadow-sm border-0"
        style={{
          position: "sticky",
          top: "75px",
          zIndex: 95,
          backgroundColor: "#ffffff",
        }}
      >
        <div className="card-body p-2">
          <Row className="g-2 align-items-center">
            <Col md="6" lg="6">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fas fa-search text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search by Employee Code, Name, Father Name, Department, Designation, Date of Joining..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ fontSize: "14px" }}
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary border-start-0"
                    type="button"
                    onClick={() => setSearchTerm("")}
                    title="Clear search"
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
            </Col>
            <Col md="6" lg="6" className="d-flex justify-content-end gap-2 align-items-center flex-wrap">
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={() => loadData(true)}
                disabled={isRefreshing || isLoading}
                title="Refresh employees from server"
                style={{ fontWeight: 500, whiteSpace: "nowrap" }}
              >
                <i className={`fas fa-sync-alt me-1 ${isRefreshing ? "fa-spin text-primary" : ""}`} />
                {isRefreshing ? "Refreshing..." : "Refresh"}
              </Button>
              <Button
                type="button"
                onClick={() => navigate("/EmployeeImportExport")}
                variant="outline-primary"
                size="sm"
                style={{ fontWeight: 600, whiteSpace: "nowrap" }}
              >
                <i className="fas fa-file-excel me-1" />
                Import / Export
              </Button>
              <Button
                type="button"
                onClick={handleAdd}
                variant="success"
                size="sm"
                style={{ fontWeight: 600, whiteSpace: "nowrap" }}
              >
                <i className="fas fa-plus me-1" />
                Add New
              </Button>
              {gridData.length > 0 && (
                <Button
                  type="button"
                  variant="outline-danger"
                  size="sm"
                  onClick={promptDeleteAll}
                  style={{ fontWeight: 600, whiteSpace: "nowrap" }}
                  title="Delete all matching or firm employees with password security"
                >
                  <i className="fas fa-trash-alt me-1" />
                  Delete All
                </Button>
              )}
            </Col>
          </Row>

          {/* ─── Bulk Action Banner (when checkboxes are selected) ─── */}
          {selectedIds.size > 0 && (
            <div
              className="mt-2 p-2 px-3 rounded d-flex flex-wrap align-items-center justify-content-between gap-2"
              style={{
                backgroundColor: "#eff6ff",
                border: "1px solid #bfdbfe",
                color: "#1e3a8a",
                animation: "fadeIn 0.2s ease-in-out",
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <span style={{ fontWeight: 600, fontSize: "13.5px" }}>
                  <i className="fas fa-check-circle text-primary me-2" />
                  <strong>{selectedIds.size}</strong> employee{selectedIds.size > 1 ? "s" : ""} selected
                </span>

                {totalItems > pageIds.length && !isAllFilteredSelected && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-primary fw-bold text-decoration-underline"
                    onClick={handleSelectAllFiltered}
                  >
                    Select all {totalItems} matching employees across all pages
                  </Button>
                )}

                {isAllFilteredSelected && (
                  <span className="badge bg-primary px-2 py-1">All {totalItems} filtered employees selected</span>
                )}
              </div>

              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={promptBulkDelete}
                  style={{ fontWeight: 600 }}
                  className="shadow-sm"
                >
                  <i className="fas fa-trash-alt me-1" />
                  Delete Selected ({selectedIds.size})
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearSelection}
                  style={{ fontWeight: 500 }}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Table Card */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-striped align-middle mb-0" style={{ fontSize: "13.5px" }}>
              <thead
                className="table-light border-bottom text-nowrap"
                style={{
                  userSelect: "none",
                  backgroundColor: "#f8fafc",
                }}
              >
                <tr>
                  {/* Select All Checkbox Column */}
                  <th style={{ width: "42px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isPageFullySelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isPagePartiallySelected;
                      }}
                      onChange={handleSelectPageToggle}
                      title="Select / Deselect all employees on this page"
                      style={{ cursor: "pointer", width: "16px", height: "16px" }}
                    />
                  </th>
                  <th
                    style={{ width: "60px", cursor: "pointer" }}
                    onClick={() => handleSort("SerialNo")}
                    title="Click to sort by Sr No"
                  >
                    Sr No {renderSortIcon("SerialNo")}
                  </th>
                  <th
                    style={{ width: "120px", cursor: "pointer" }}
                    onClick={() => handleSort("EmployeeCode")}
                    title="Click to sort by Employee Code"
                  >
                    Emp. Code {renderSortIcon("EmployeeCode")}
                  </th>
                  <th
                    style={{ minWidth: "160px", cursor: "pointer" }}
                    onClick={() => handleSort("Name")}
                    title="Click to sort by Name"
                  >
                    Name {renderSortIcon("Name")}
                  </th>
                  <th
                    style={{ minWidth: "140px", cursor: "pointer" }}
                    onClick={() => handleSort("FatherName")}
                    title="Click to sort by Father Name"
                  >
                    Father Name {renderSortIcon("FatherName")}
                  </th>
                  <th
                    style={{ minWidth: "130px", cursor: "pointer" }}
                    onClick={() => handleSort("DepartmentName")}
                    title="Click to sort by Department"
                  >
                    Department {renderSortIcon("DepartmentName")}
                  </th>
                  <th
                    style={{ minWidth: "120px", cursor: "pointer" }}
                    onClick={() => handleSort("JoinDate")}
                    title="Click to sort by Joining Date"
                  >
                    Joining Date {renderSortIcon("JoinDate")}
                  </th>
                  <th
                    style={{ minWidth: "100px", cursor: "pointer" }}
                    onClick={() => handleSort("EmployeeType")}
                    title="Click to sort by Employee Type"
                  >
                    Emp. Type {renderSortIcon("EmployeeType")}
                  </th>
                  <th style={{ width: "100px", textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Active Loading State */}
                {isLoading && gridData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-5">
                      <div className="py-4">
                        <div className="spinner-border text-primary mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <h6 className="fw-bold mb-1">Loading employee records from server...</h6>
                        <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                          Firms with many employees may take a moment to download initially.
                          <br />
                          Data is cached in memory for instant loading on your next visits!
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((row) => {
                    const isSelected = selectedIds.has(row.Id);
                    return (
                      <tr
                        key={row.Id || row.SerialNo}
                        style={{
                          backgroundColor: isSelected ? "#f0f7ff" : undefined,
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        {/* Row Checkbox */}
                        <td style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isSelected}
                            onChange={(e) => handleRowCheckbox(row.Id, e)}
                            style={{ cursor: "pointer", width: "16px", height: "16px" }}
                          />
                        </td>
                        <td>
                          <strong className="text-secondary">{row.SerialNo}</strong>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border font-monospace px-2 py-1">
                            {row.EmployeeCode || "-"}
                          </span>
                        </td>
                        <td>
                          <strong className="text-dark">{row.Name}</strong>
                        </td>
                        <td className="text-muted">{row.FatherName || "-"}</td>
                        <td>{row.DepartmentName || "-"}</td>
                        <td>{row.JoinDate || "-"}</td>
                        <td>
                          <span className="badge bg-info-subtle text-info border border-info-subtle">
                            {row.EmployeeType || "-"}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <div className="d-flex justify-content-center gap-1">
                            <Button
                              variant="primary"
                              size="sm"
                              className="p-1 px-2"
                              onClick={() => handleEdit(row.Id)}
                              title="Edit Employee"
                            >
                              <i className="fa fa-edit" />
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              className="p-1 px-2"
                              onClick={() => promptSingleDelete(row)}
                              title="Delete Employee"
                            >
                              <i className="fa fa-trash" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-5 text-muted">
                      <div className="py-3">
                        <i className="fas fa-users-slash fa-3x mb-3 text-secondary opacity-50 d-block" />
                        <h6 className="fw-semibold">No Employees Found</h6>
                        <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                          {debouncedSearch
                            ? `No employee records match "${searchTerm}".`
                            : "No employee records exist for this firm."}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Controls: Pagination & Page Size */}
          <div className="d-flex flex-wrap align-items-center justify-content-between p-3 border-top gap-2 bg-light bg-opacity-50 flex-shrink-0">
            {/* Record Summary */}
            <div className="text-muted" style={{ fontSize: "13px" }}>
              {totalItems > 0 ? (
                <>
                  Showing <strong>{startIndex}</strong> to <strong>{endIndex}</strong> of{" "}
                  <strong>{totalItems.toLocaleString()}</strong> employees
                  {totalItems !== gridData.length && (
                    <span className="text-muted ms-1">(filtered from {gridData.length.toLocaleString()} total)</span>
                  )}
                </>
              ) : (
                "0 records"
              )}
            </div>

            {/* Pagination Controls & Page Size Selector */}
            <div className="d-flex align-items-center gap-3">
              {/* Page Size Selector */}
              <div className="d-flex align-items-center gap-1" style={{ fontSize: "13px" }}>
                <span className="text-muted">Rows:</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "75px" }}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                  <option value={-1}>All</option>
                </select>
              </div>

              {/* Page Navigation Buttons */}
              {pageSize !== -1 && totalPages > 1 && (
                <div className="btn-group btn-group-sm">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setCurrentPage(1)}
                    disabled={safePage <= 1}
                    title="First Page"
                  >
                    {"<<"}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    title="Previous Page"
                  >
                    {"<"}
                  </Button>

                  <span className="btn btn-outline-secondary disabled text-dark px-3" style={{ fontWeight: 600 }}>
                    {safePage} / {totalPages}
                  </span>

                  <Button
                    variant="outline-secondary"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    title="Next Page"
                  >
                    {">"}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safePage >= totalPages}
                    title="Last Page"
                  >
                    {">>"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── 1. SECURE DELETION CONFIRMATION & PASSWORD VERIFICATION MODAL ─── */}
      <Modal
        show={showDeleteModal}
        onHide={() => !isDeleting && setShowDeleteModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!isDeleting} className="bg-danger text-white">
          <Modal.Title className="h5 fw-bold mb-0">
            <i className="fas fa-exclamation-triangle me-2" />
            Confirm Employee Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {!isDeleting ? (
            <>
              {/* Step 1: Warning Message */}
              <div className="alert alert-danger d-flex align-items-start gap-2 mb-3">
                <i className="fas fa-radiation-alt fa-2x flex-shrink-0 mt-1" />
                <div>
                  <strong>Permanent Data Removal Warning:</strong>
                  <p className="mb-0 mt-1" style={{ fontSize: "13px" }}>
                    Are you sure you want to delete{" "}
                    <strong className="text-dark bg-white px-2 py-0.5 rounded border">
                      {deleteTarget.totalCount} employee record{deleteTarget.totalCount > 1 ? "s" : ""}
                    </strong>
                    ? This will delete all master entries, department & shift mappings, salary history, and allowances.
                  </p>
                </div>
              </div>

              {/* Target Preview */}
              <div
                className="bg-light p-2 px-3 rounded border mb-3"
                style={{ maxHeight: "110px", overflowY: "auto", fontSize: "12.5px" }}
              >
                <div className="text-muted fw-bold mb-1">Records to be deleted:</div>
                <div className="d-flex flex-wrap gap-1">
                  {deleteTarget.items.slice(0, 15).map((e) => (
                    <span key={e.Id} className="badge bg-secondary">
                      {e.EmployeeCode || e.SerialNo}: {e.Name}
                    </span>
                  ))}
                  {deleteTarget.items.length > 15 && (
                    <span className="badge bg-dark">+{deleteTarget.items.length - 15} more</span>
                  )}
                </div>
              </div>

              {/* Reason / Notes (Optional) */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ fontSize: "13px" }}>
                  Reason for Deletion (Audit Trail Note):
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Bulk cleanup of test data / Resigned batch..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  style={{ fontSize: "13.5px" }}
                />
              </Form.Group>

              {/* Step 2: Master Security Password Verification */}
              <Form.Group className="mb-2">
                <Form.Label className="fw-bold text-danger d-flex justify-content-between align-items-center" style={{ fontSize: "13px" }}>
                  <span>
                    <i className="fas fa-lock me-1" />
                    Enter Security Master Password:
                  </span>
                  <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
                    <i className="fas fa-shield-alt me-1" />
                    Authorization Required
                  </span>
                </Form.Label>
                <div className="input-group">
                  <Form.Control
                    ref={passwordInputRef}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter security authorization password"
                    value={securityPassword}
                    onChange={(e) => {
                      setSecurityPassword(e.target.value);
                      setPasswordError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleConfirmDelete();
                    }}
                    isInvalid={!!passwordError}
                    style={{ fontSize: "14px", letterSpacing: showPassword ? "normal" : "2px" }}
                    autoFocus
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                  </Button>
                </div>
                <Form.Text className="text-muted" style={{ fontSize: "11px" }}>
                  <i className="fas fa-info-circle me-1" />
                  Please enter the master delete password configured in <strong>Payroll Parameter</strong>.
                </Form.Text>
                {passwordError && (
                  <div className="text-danger mt-1 fw-semibold" style={{ fontSize: "12px" }}>
                    <i className="fas fa-times-circle me-1" />
                    {passwordError}
                  </div>
                )}
              </Form.Group>
            </>
          ) : (
            /* Progress state */
            <div className="text-center py-4">
              <div className="spinner-border text-danger mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
                <span className="visually-hidden">Deleting...</span>
              </div>
              <h5 className="fw-bold mb-1">Deleting Employee Records...</h5>
              <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                Processed <strong>{deleteProgress.current}</strong> of <strong>{deleteProgress.total}</strong> records
              </p>
              <ProgressBar
                now={deleteProgress.percentage}
                label={`${deleteProgress.percentage}%`}
                variant="danger"
                animated
                style={{ height: "20px" }}
              />
            </div>
          )}
        </Modal.Body>
        {!isDeleting && (
          <Modal.Footer className="bg-light">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" className="fw-bold shadow-sm" onClick={handleConfirmDelete}>
              <i className="fas fa-trash-alt me-1" />
              Verify & Delete Permanently
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* ─── 2. DELETION RESULT REPORT MODAL (When references exist) ─── */}
      <Modal show={showResultModal} onHide={() => setShowResultModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-warning-subtle border-warning">
          <Modal.Title className="h5 fw-bold text-dark mb-0">
            <i className="fas fa-clipboard-check me-2 text-warning" />
            Deletion Summary Report
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="d-flex gap-3 mb-3">
            <div className="flex-fill p-3 rounded bg-success-subtle border border-success-subtle text-center">
              <div className="h3 fw-bold text-success mb-0">{deletionResults.successCount}</div>
              <div className="text-success fw-semibold" style={{ fontSize: "12px" }}>
                Successfully Deleted
              </div>
            </div>
            <div className="flex-fill p-3 rounded bg-danger-subtle border border-danger-subtle text-center">
              <div className="h3 fw-bold text-danger mb-0">{deletionResults.failedCount}</div>
              <div className="text-danger fw-semibold" style={{ fontSize: "12px" }}>
                Could Not Delete (Protected)
              </div>
            </div>
          </div>

          {deletionResults.failedItems.length > 0 && (
            <div>
              <h6 className="fw-bold text-dark mb-2">Protected Employees (Reference Exists):</h6>
              <div className="table-responsive" style={{ maxHeight: "250px", overflowY: "auto" }}>
                <table className="table table-sm table-bordered align-middle" style={{ fontSize: "12.5px" }}>
                  <thead className="table-light">
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Reason / Conflict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletionResults.failedItems.map((f, i) => (
                      <tr key={i}>
                        <td className="font-monospace fw-bold">{f.employee?.EmployeeCode || f.employee?.SerialNo}</td>
                        <td>{f.employee?.Name}</td>
                        <td className="text-danger">{f.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted mt-2 mb-0" style={{ fontSize: "12px" }}>
                <i className="fas fa-info-circle me-1" />
                These records have linked salary vouchers, attendance logs, or loan disbursements and are protected
                from accidental deletion.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="primary" onClick={() => setShowResultModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── 3. HISTORICAL DELETE AUDIT LOG MODAL ─── */}
      <Modal show={showAuditModal} onHide={() => setShowAuditModal(false)} size="xl" centered>
        <Modal.Header closeButton className="bg-dark text-white">
          <Modal.Title className="h5 fw-bold mb-0">
            <i className="fas fa-history me-2 text-warning" />
            Employee Deletion Audit Logs & History
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4" style={{ backgroundColor: "#f8fafc" }}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="input-group" style={{ maxWidth: "380px" }}>
              <span className="input-group-text bg-white">
                <i className="fas fa-search text-muted" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search logs by user, date, reason, or employee..."
                value={auditSearch}
                onChange={(e) => setAuditSearch(e.target.value)}
                style={{ fontSize: "13px" }}
              />
              {auditSearch && (
                <button className="btn btn-outline-secondary" onClick={() => setAuditSearch("")}>
                  ✕
                </button>
              )}
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => handleExportAuditLogs("csv")}
                style={{ fontWeight: 600 }}
              >
                <i className="fas fa-file-csv me-1" />
                Export CSV
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleExportAuditLogs("json")}
                style={{ fontWeight: 600 }}
              >
                <i className="fas fa-file-code me-1" />
                Export JSON
              </Button>
            </div>
          </div>

          {filteredAuditLogs.length > 0 ? (
            <div className="table-responsive bg-white rounded shadow-sm border" style={{ maxHeight: "55vh", overflowY: "auto" }}>
              <Table hover className="align-middle mb-0" style={{ fontSize: "13px" }}>
                <thead className="table-light border-bottom sticky-top">
                  <tr>
                    <th>Date & Time</th>
                    <th>Action Type</th>
                    <th>Deleted Count</th>
                    <th>Deleted By</th>
                    <th>Reason / Note</th>
                    <th>Deleted Employees</th>
                    <th style={{ textAlign: "center" }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAuditLogs.map((log) => (
                    <tr key={log.id}>
                      <td className="text-nowrap fw-bold text-secondary">{log.dateStr}</td>
                      <td>
                        <span
                          className={`badge ${
                            log.actionType.includes("All")
                              ? "bg-danger"
                              : log.actionType.includes("Bulk")
                              ? "bg-warning text-dark"
                              : "bg-info"
                          }`}
                        >
                          {log.actionType}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border fw-bold px-2 py-1">
                          {log.successCount} record{log.successCount > 1 ? "s" : ""}
                        </span>
                        {log.failedCount > 0 && (
                          <span className="badge bg-danger-subtle text-danger border ms-1">
                            {log.failedCount} failed
                          </span>
                        )}
                      </td>
                      <td>
                        <strong>{log.userName}</strong>
                        <div className="text-muted" style={{ fontSize: "11px" }}>
                          UID: {log.userId}
                        </div>
                      </td>
                      <td style={{ maxWidth: "220px", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                        {log.reason || "-"}
                      </td>
                      <td style={{ maxWidth: "250px" }}>
                        <div className="d-flex flex-wrap gap-1">
                          {(log.deletedEmployees || []).slice(0, 3).map((e, idx) => (
                            <span key={idx} className="badge bg-light text-secondary border font-monospace">
                              {e.code}: {e.name}
                            </span>
                          ))}
                          {(log.deletedEmployees || []).length > 3 && (
                            <span className="badge bg-secondary">
                              +{(log.deletedEmployees || []).length - 3} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="py-0 px-2"
                          onClick={() => setSelectedLogDetail(log)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-5 bg-white rounded border text-muted">
              <i className="fas fa-clipboard-list fa-3x mb-3 text-secondary opacity-50 d-block" />
              <h6>No Deletion Logs Found</h6>
              <p className="mb-0" style={{ fontSize: "13px" }}>
                Deletions executed with the security password will be automatically logged here for auditing.
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={() => setShowAuditModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── 4. AUDIT LOG DETAIL SUB-MODAL ─── */}
      <Modal
        show={!!selectedLogDetail}
        onHide={() => setSelectedLogDetail(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="h5 fw-bold mb-0">
            <i className="fas fa-file-alt me-2" />
            Deletion Audit Log Detail
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {selectedLogDetail && (
            <div>
              <Row className="g-2 mb-3">
                <Col md="6">
                  <div className="p-2 bg-light rounded border">
                    <span className="text-muted d-block" style={{ fontSize: "11px" }}>LOG ID</span>
                    <strong className="font-monospace">{selectedLogDetail.id}</strong>
                  </div>
                </Col>
                <Col md="6">
                  <div className="p-2 bg-light rounded border">
                    <span className="text-muted d-block" style={{ fontSize: "11px" }}>DATE & TIME</span>
                    <strong>{selectedLogDetail.dateStr}</strong>
                  </div>
                </Col>
                <Col md="6">
                  <div className="p-2 bg-light rounded border">
                    <span className="text-muted d-block" style={{ fontSize: "11px" }}>PERFORMED BY</span>
                    <strong>{selectedLogDetail.userName} (ID: {selectedLogDetail.userId})</strong>
                  </div>
                </Col>
                <Col md="6">
                  <div className="p-2 bg-light rounded border">
                    <span className="text-muted d-block" style={{ fontSize: "11px" }}>ACTION & REASON</span>
                    <strong>{selectedLogDetail.actionType}</strong> - {selectedLogDetail.reason}
                  </div>
                </Col>
              </Row>

              <h6 className="fw-bold text-dark mb-2">
                Deleted Employees ({selectedLogDetail.deletedEmployees?.length || 0}):
              </h6>
              <div className="table-responsive" style={{ maxHeight: "250px", overflowY: "auto" }}>
                <table className="table table-sm table-bordered align-middle" style={{ fontSize: "12.5px" }}>
                  <thead className="table-light">
                    <tr>
                      <th>Emp. Code</th>
                      <th>Employee Name</th>
                      <th>Father Name</th>
                      <th>Department</th>
                      <th>Designation</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedLogDetail.deletedEmployees || []).map((e, idx) => (
                      <tr key={idx}>
                        <td className="font-monospace fw-bold">{e.code || "-"}</td>
                        <td>{e.name}</td>
                        <td className="text-muted">{e.fatherName || "-"}</td>
                        <td>{e.dept || "-"}</td>
                        <td>{e.desig || "-"}</td>
                        <td>{renderStatusBadge(e.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={() => setSelectedLogDetail(null)}>
            Back to Logs
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PageList_EmployeeMaster;
